import React, { useState, useEffect } from "react";
import { Calendar } from "lucide-react";

const getTodayDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const EditAssetDrawer = ({ asset, onClose, onSave, errorMessage = "" }) => {
  const today = getTodayDateString();
  const formatDateValue = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toISOString().split("T")[0];
  };

  const getStatusValue = (status) => {
    if (!status) return "Active";
    return status === "RETURNED" ? "Return" : "Active";
  };

  const profilePicture =
    asset?.resource?.profilePicture || asset?.resource?.image || null;
  const resourceName = asset?.resource?.name || "Unassigned";
  const modelName =
    asset?.modelName ||
    asset?.inventoryItem?.modelName ||
    asset?.assetName ||
    "-";
  const serialNumber =
    asset?.serialNumber ||
    asset?.inventoryItem?.serialNumber ||
    asset?.serial ||
    "-";
  const brand = asset?.brand || asset?.inventoryItem?.brand || "-";
  const assetLabel =
    serialNumber && serialNumber !== "-"
      ? `${modelName} — ${serialNumber}`
      : modelName;
  const warrantyExpiry =
    asset?.warrantyExpiry || asset?.inventoryItem?.warrantyExpiry;

  const [formData, setFormData] = useState({
    assetType: asset?.category || asset?.type || "Laptop",
    selectedAsset: modelName,
    status: getStatusValue(asset?.status),
    assignedDate: formatDateValue(asset?.assignDate),
    returnDate: formatDateValue(asset?.returnDate),
  });

  useEffect(() => {
    setFormData({
      assetType: asset?.category || asset?.type || "Laptop",
      selectedAsset: modelName,
      status: getStatusValue(asset?.status),
      assignedDate: formatDateValue(asset?.assignDate),
      returnDate: formatDateValue(asset?.returnDate),
    });
  }, [asset, modelName]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    onSave?.({
      category: formData.assetType,
      assignDate: formData.assignedDate,
      returnDate: formData.returnDate,
      status: formData.status === "Return" ? "RETURNED" : "ACTIVE",
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/20">
      {/* Right Drawer */}
      <div className="w-full max-w-[430px] bg-white h-full p-6 overflow-y-auto">
        {/* Header */}
        <h2 className="text-[28px] font-semibold">Edit Asset</h2>

        <p className="text-sm text-gray-400 mt-1">Update asset details</p>

        {/* User Card */}
        <div className="mt-6 bg-[#F5F7FB] rounded-lg p-4 flex gap-3">
          {profilePicture ? (
            <img
              src={profilePicture}
              alt=""
              className="w-14 h-14 rounded-full object-cover"
            />
          ) : (
            <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center text-lg font-bold text-blue-600">
              {resourceName[0]}
            </div>
          )}

          <div>
            <h3 className="font-medium">{resourceName}</h3>

            <p className="text-xs text-gray-400">
              {asset?.resource?.email || "-"}
            </p>

            <p className="text-xs text-gray-400">{asset?.empId || "-"}</p>
          </div>
        </div>

        {/* Asset Details */}
        <h4 className="mt-6 text-sm font-semibold">ASSET DETAILS</h4>

        <hr className="my-3" />

        {/* Asset Type */}
        <div className="mb-4">
          <label className="text-sm">Asset Type</label>

          <select
            name="assetType"
            className="mt-2 w-full h-11 border rounded-lg px-3"
            value={formData.assetType}
            onChange={handleChange}
          >
            <option>Laptop</option>
            <option>Mobile</option>
            <option>ID Card</option>
            <option>Headset</option>
            <option>Broadband</option>
          </select>
        </div>

        {/* Select Asset */}
        <div className="mb-4">
          <label className="text-sm">Select Asset</label>

          <select
            name="selectedAsset"
            className="mt-2 w-full h-11 border rounded-lg px-3"
            value={formData.selectedAsset}
            onChange={handleChange}
          >
            <option value={modelName}>{assetLabel}</option>
          </select>
        </div>

        {/* Status */}
        <div className="mb-4">
          <label className="text-sm">Status</label>

          <select
            name="status"
            className="mt-2 w-full h-11 border rounded-lg px-3"
            value={formData.status}
            onChange={handleChange}
          >
            <option value="Return">Return</option>
            <option value="Active">Active</option>
          </select>
        </div>

        {/* Assigned Date */}
        <div className="mb-4">
          <label className="text-sm">Assigned Date</label>

          <div className="relative mt-2">
            <Calendar
              size={16}
              className="
                absolute left-3 top-1/2
                -translate-y-1/2
                text-[#4C5CE7]
              "
            />

            <input
              type="date"
              name="assignedDate"
              value={formData.assignedDate}
              onChange={handleChange}
              className="
                w-full h-11 border rounded-lg
                pl-10 px-3
              "
            />
          </div>
        </div>

        {/* Return Date */}
        <div className="mb-5">
          <label className="text-sm">Return Date</label>

          <div className="relative mt-2">
            <Calendar
              size={16}
              className="
                absolute left-3 top-1/2
                -translate-y-1/2
                text-[#4C5CE7]
              "
            />

            <input
              type="date"
              name="returnDate"
              min={formData.assignedDate || today}
              value={formData.returnDate}
              onChange={handleChange}
              className="
                w-full h-11 border rounded-lg
                pl-10 px-3
              "
            />
          </div>
        </div>

        {/* Info Boxes */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-[#F5F7FB] rounded-lg p-3">
            <p className="text-xs text-gray-400">Brand</p>

            <p className="text-sm">{brand}</p>
          </div>

          <div className="bg-[#F5F7FB] rounded-lg p-3">
            <p className="text-xs text-gray-400">Model Name</p>

            <p className="text-sm">{modelName}</p>
          </div>

          <div className="bg-[#F5F7FB] rounded-lg p-3">
            <p className="text-xs text-gray-400">Serial Number</p>

            <p className="text-sm">{serialNumber}</p>
          </div>

          <div className="bg-[#F5F7FB] rounded-lg p-3">
            <p className="text-xs text-gray-400">Warranty Expiry</p>

            <p className="text-sm">
              {warrantyExpiry ? formatDateValue(warrantyExpiry) : "-"}
            </p>
          </div>
        </div>

        {errorMessage ? (
          <p className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
            {errorMessage}
          </p>
        ) : null}

        {/* Footer Buttons */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="
              flex-1 h-11
              border border-[#4C5CE7]
              rounded-lg
              text-[#4C5CE7]
            "
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            className="
              flex-1 h-11
              bg-[#4C5CE7]
              text-white
              rounded-lg
            "
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditAssetDrawer;
